import {Injector} from '@angular/core';
import {Subject, timer} from 'rxjs';
import {takeUntil} from 'rxjs/operators';
import {LockableResources} from '@pt/pt-models';
import {DocumentLockResponse} from "@primatoo-ms-organization";
import { DataBreachProcessService } from './services/home/data-breach-process.service';
import { TreatmentService } from './services/home/documents/treatment.service';
import { AiAssessmentsService } from './services/home/documents/ai-assessments.service';
import { GlobalMeasureChecksService } from './services/home/documents/global-measure-checks.service';
import { AppAssessmentsService } from './services/home/documents/app-assessments.service';
import { CovidAssessmentsService } from './services/home/documents/covid-assessments.service';

export class ResourceLockManager {
  private lockableResource: LockableResources;
  private injector: Injector;
  private readonly resourceId: number;
  private readonly TOKEN_RESOURCE_KEY_PREFIX: string;
  private lockAcquired$ = new Subject<boolean>();
  private _lockAcquired = true;
  private destroy$: Subject<boolean> = new Subject<boolean>();

  constructor(lockableResource: LockableResources,
              resourceId: number,
              checkEveryInSec: number,
              injector: Injector) {

    this.lockableResource = lockableResource;
    this.injector = injector;
    this.resourceId = resourceId;
    this.TOKEN_RESOURCE_KEY_PREFIX = `lock_${lockableResource}_`;

    this.lockAcquired$.next(this._lockAcquired);
    timer(0, checkEveryInSec * 1000).pipe(
      takeUntil(this.destroy$)
    ).subscribe(() => {
      const currentToken = this.getLockTokenToSessionStorage(this.resourceId);
      this.getLockFn(lockableResource, resourceId, currentToken).subscribe((resp: DocumentLockResponse) => {
        this.writeLockToSessionStorage(resp.docId, resp.token);
        this._lockAcquired = true;
        this.lockAcquired$.next(this._lockAcquired);
      }, () => {
        this.removeLockToSessionStorage(this.resourceId);
        this._lockAcquired = false;
        this.lockAcquired$.next(this._lockAcquired);
      });
    });
  }

  private getLockFn(lockableResource: LockableResources, resourceId: number, token: number) {
    switch (lockableResource) {
      case 'dataBreach':
        return this.injector.get<DataBreachProcessService>(DataBreachProcessService).lockResource(resourceId, token);
      case 'treatment':
        return this.injector.get<TreatmentService>(TreatmentService).lockResource(resourceId, token);
      case 'aiAssessment':
        return this.injector.get<AiAssessmentsService>(AiAssessmentsService).lockResource(resourceId, token);
      case 'globalMeasureChecks':
        return this.injector.get<GlobalMeasureChecksService>(GlobalMeasureChecksService).lockResource(resourceId, token);
      case 'appAssessment':
        return this.injector.get<AppAssessmentsService>(AppAssessmentsService).lockResource(resourceId, token);
      case 'covidAssessment':
        return this.injector.get<CovidAssessmentsService>(CovidAssessmentsService).lockResource(resourceId, token);
    }
  }

  private getReleaseLockFn(lockableResource: LockableResources, resourceId: number) {
    switch (lockableResource) {
      case 'dataBreach':
        return this.injector.get<DataBreachProcessService>(DataBreachProcessService).releaseLock(resourceId);
      case 'treatment':
        return this.injector.get<TreatmentService>(TreatmentService).releaseLock(resourceId);
      case 'aiAssessment':
        return this.injector.get<AiAssessmentsService>(AiAssessmentsService).releaseLock(resourceId);
      case 'globalMeasureChecks':
        return this.injector.get<GlobalMeasureChecksService>(GlobalMeasureChecksService).releaseLock(resourceId);
      case 'appAssessment':
        return this.injector.get<AppAssessmentsService>(AppAssessmentsService).releaseLock(resourceId);
      case 'covidAssessment':
        return this.injector.get<CovidAssessmentsService>(CovidAssessmentsService).releaseLock(resourceId);
    }
  }

  public get resourceLockAcquired(): Subject<boolean> {
    return this.lockAcquired$;
  }

  public release() {
    this.destroy$.next(true);
    if (this._lockAcquired) {
      this._lockAcquired = false;
      this.lockAcquired$.next(this._lockAcquired);
      this.getReleaseLockFn(this.lockableResource, this.resourceId).subscribe(() => {
        this.removeLockToSessionStorage(this.resourceId);
      });
    }
  }

  private writeLockToSessionStorage(resourceId: number, token: number): void {
    sessionStorage.setItem(`${this.TOKEN_RESOURCE_KEY_PREFIX}_${resourceId}`, token.toString());
  }

  private removeLockToSessionStorage(resourceId): void {
    sessionStorage.removeItem(`${this.TOKEN_RESOURCE_KEY_PREFIX}_${resourceId}`);
  }

  private getLockTokenToSessionStorage(resourceId): number {
    const t = Number(sessionStorage.getItem(`${this.TOKEN_RESOURCE_KEY_PREFIX}_${resourceId}`));
    if (t !== null && t > 0) {
      return t;
    }
    return null;
  }
}
